import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api.js";
import type { TimelineEntry, EmployeeDay, LiveActivity, Employee } from "../types.js";
import { PeoplePanel } from "./PeoplePanel.js";
import { DevicesPanel } from "./DevicesPanel.js";
import { Icon } from "../components/Icon.js";
import { hoursAndMinutes } from "../lib/duration.js";
import { berlinClock, berlinDay, berlinHoursInto } from "../lib/berlin.js";

/**
 * A day, and what each person recorded on it.
 *
 * Worth being honest in the code about what this screen is. SPEC.md asks
 * for per-person breakdowns to be deliberately constructed rather than a
 * default view, and for nothing to place task time where it reads as
 * attendance. A daily timeline is both, and the shop asked for it
 * knowingly.
 *
 * So the screen does what it can to not overstate. It draws the spans
 * that were recorded and leaves the rest of the day blank — a gap is
 * time that was not tracked, which is not the same as time not worked,
 * and the spec says as much. There is no daily total, no percentage and
 * no ranking: those are the numbers that would turn this into the thing
 * the spec warns about.
 */

// The window a day is drawn in, unless its work falls outside it. Fixed
// at 06–22 until 2026-09-26, which drew nothing for a session wholly
// outside it while the row still counted it in "tracked", and cut a
// session running past 22:00 off as though it had ended there.
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 22;

type Window = { start: number; end: number; span: number };

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

/**
 * Where an entry starts and ends on this day's wall clock, in Berlin.
 * Wall clock, not hours elapsed since midnight: on the two
 * daylight-saving days those differ by one, and every bar after 03:00
 * was drawn an hour away from its label (lib/berlin.ts).
 */
function hoursOf(date: string, entry: TimelineEntry, now: number) {
  const from = berlinHoursInto(date, entry.startedAt);
  const to = berlinHoursInto(date, entry.endedAt ?? new Date(now));
  return { from, to };
}

/**
 * 06–22, widened to whatever the day actually holds — on even hours, so
 * the labels land on the lines — but never past midnight either side:
 * that part of a session belongs to the neighbouring day's view.
 */
function windowFor(date: string, day: EmployeeDay[], now: number): Window {
  let start = DAY_START_HOUR;
  let end = DAY_END_HOUR;
  for (const person of day) {
    for (const entry of person.entries) {
      const { from, to } = hoursOf(date, entry, now);
      start = Math.min(start, Math.max(0, Math.floor(from / 2) * 2));
      end = Math.max(end, Math.min(24, Math.ceil(to / 2) * 2));
    }
  }
  return { start, end, span: end - start };
}

/** Where a span sits on the bar, clipped to the window shown. */
function place(date: string, entry: TimelineEntry, now: number, win: Window) {
  const { from, to } = hoursOf(date, entry, now);

  const left = Math.max(from, win.start);
  const right = Math.min(to, win.end);
  if (right <= left) return null;

  return {
    left: ((left - win.start) / win.span) * 100,
    width: ((right - left) / win.span) * 100,
    clippedStart: from < win.start,
    clippedEnd: to > win.end,
  };
}

const clock = berlinClock;

function duration(seconds: number | null): string {
  return seconds === null ? "running" : hoursAndMinutes(seconds);
}

/** What a bar was, in one line: said on tap, and read out by a screen reader. */
function spanDetail(person: string, entry: TimelineEntry): string {
  const task = `${entry.task}${entry.label ? ` (${entry.label})` : ""}`;
  const span = `${clock(entry.startedAt)}–${entry.endedAt ? clock(entry.endedAt) : "now"}`;
  return `${person} · ${task} · ${span} · ${duration(entry.seconds)}`;
}

/** Stable colour per task, so one activity reads the same all the way down. */
function hue(task: string): number {
  let hash = 0;
  for (const ch of task) hash = (hash * 31 + ch.charCodeAt(0)) % 360;
  return hash;
}

export function DashboardScreen({
  me,
  onBack,
}: {
  me: Employee;
  onBack: () => void;
}) {
  // The shop's today, not the device's.
  const today = useMemo(() => berlinDay(new Date()), []);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [date, setDate] = useState(today);
  const [activeDays, setActiveDays] = useState<string[]>([]);
  const [day, setDay] = useState<EmployeeDay[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState<LiveActivity[] | null>(null);
  // The export range. Seeded from the selected day and re-seeded
  // whenever it changes, so the common case — "give me the day I am
  // looking at" — needs no thought and the boxes never disagree with
  // the timeline underneath them. Typing a range and then clicking a
  // day loses the range, which is the cheaper mistake: the alternative
  // is a button that quietly exports a period nobody is looking at.
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const now = useMemo(() => Date.now(), [date]);
  // Four sections rather than one long page. People and Devices mount
  // only when opened, so the screen an admin checks most — who is
  // tracking right now — no longer waits behind two lists it did not ask
  // for.
  const [tab, setTab] = useState<"activity" | "calendar" | "people" | "devices">(
    "activity",
  );

  useEffect(() => {
    let cancelled = false;
    api
      .dashboardDays(month)
      .then(({ days }) => {
        if (!cancelled) setActiveDays(days);
      })
      .catch(() => {
        if (!cancelled) setActiveDays([]);
      });
    return () => {
      cancelled = true;
    };
  }, [month]);

  // Refreshed on a timer while the screen is open, and only while it is
  // open. Nothing polls in the background: this is a page somebody
  // deliberately opens, not a wallboard that runs in a corner, and that
  // distinction is one of the things holding the line described in
  // README under "The live board".
  useEffect(() => {
    let cancelled = false;
    const tick = () =>
      api
        .dashboardLive()
        .then(({ activities }) => {
          if (!cancelled) setLive(activities);
        })
        .catch(() => {
          if (!cancelled) setLive(null);
        });
    void tick();
    const id = setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const [picked, setPicked] = useState<string | null>(null);
  const win = useMemo(() => (day ? windowFor(date, day, now) : null), [date, day, now]);
  const pickedDetail = useMemo(() => {
    for (const person of day ?? []) {
      const entry = person.entries.find((e) => e.sessionId === picked);
      if (entry) return spanDetail(person.displayName, entry);
    }
    return null;
  }, [day, picked]);

  // Only the latest request may write. Without this — the calendar and
  // live fetches above both have a guard; this had none — a slow answer
  // for the day just left arrived last and put its people under the new
  // day's title.
  const latest = useRef(0);
  const load = useCallback(async (which: string) => {
    const mine = ++latest.current;
    setError(null);
    setDay(null);
    setPicked(null);
    try {
      const { employees } = await api.dashboardDay(which);
      if (mine === latest.current) setDay(employees);
    } catch {
      if (mine === latest.current) setError("Could not load that day.");
    }
  }, []);

  useEffect(() => {
    void load(date);
  }, [date, load]);

  const grid = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y!, m! - 1, 1);
    const days: (string | null)[] = [];
    // Monday-first, which is what a German shop reads.
    const lead = (first.getDay() + 6) % 7;
    for (let i = 0; i < lead; i++) days.push(null);
    const last = new Date(y!, m!, 0).getDate();
    for (let d = 1; d <= last; d++) days.push(iso(new Date(y!, m! - 1, d)));
    return days;
  }, [month]);

  const shiftMonth = (by: number) => {
    const [y, m] = month.split("-").map(Number);
    const next = new Date(y!, m! - 1 + by, 1);
    setMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`);
  };

  useEffect(() => {
    setFrom(date);
    setTo(date);
    // Stepping day by day can cross into another month; the calendar
    // follows, so opening it shows the day being looked at.
    setMonth(date.slice(0, 7));
  }, [date]);

  // Day by day from the timeline itself, without a trip to the calendar.
  // Never past today: a future day is always empty, and an empty day is
  // the one thing on this screen that is easy to misread.
  const stepDay = (by: number) => {
    const [y, m, d] = date.split("-").map(Number);
    const next = iso(new Date(y!, m! - 1, d! + by));
    if (next <= today) setDate(next);
  };

  // A backwards range is not an error the server reports — it returns a
  // file with a header row and nothing under it, which reads as "there
  // was no work" rather than "you asked the wrong question".
  const rangeValid = from !== "" && to !== "" && from <= to;

  const exportUrl = (what: "sessions" | "jobs") =>
    `/api/export/${what}.csv?from=${from}&to=${to}`;

  const dayTitle = new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <main className="wide-main">
      <header className="title-with-back">
        <button type="button" className="ghost" onClick={onBack}>
          <Icon name="back" size={26} />
          Back
        </button>
        <h1>Admin</h1>
      </header>

      <nav className="tabs" aria-label="Admin sections">
        {(
          [
            ["activity", "Activity"],
            ["calendar", "Calendar"],
            ["people", "People"],
            ["devices", "Devices"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* The staff live here rather than in Manage, and so does the
          restriction: this screen is admin only and the server says the
          same about the routes behind it. See PeoplePanel. */}
      {tab === "people" ? <PeoplePanel me={me} /> : null}

      {/* Devices are here for the same reason people are: a device token
          opens everything, so registering one is an admin's job and the
          routes behind this say so too. */}
      {tab === "devices" ? <DevicesPanel /> : null}

      {tab === "activity" ? (
        <>
          <section className="card">
            <div className="row">
              <h2>Right now</h2>
              <p className="status-line compact">
                <span className="status-dot live" aria-hidden="true" />
                Updates every 30 s
              </p>
            </div>
            {live === null ? (
              <p className="muted">Not loaded.</p>
            ) : live.length === 0 ? (
              <p className="muted">Nobody is tracking anything at the moment.</p>
            ) : (
              <ul className="joblist">
                {live.map((a) => (
                  <li key={a.jobId} className="live-row">
                    {a.label ? <code>{a.label}</code> : null}
                    <span className="item-title">{a.name}</span>
                    <span className="chips">
                      {a.employees.map((e) => (
                        <span key={e.employeeId} className="chip">
                          <strong>{e.displayName}</strong> since{" "}
                          {berlinClock(e.since)}
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {/* Said on the screen, not only in the documentation. Somebody
                reading this has to know what the blanks mean before they
                read anything into them. */}
            <p className="hint">
              Only open timers, grouped by what is being worked on. Somebody
              not listed is not tracking — which is not the same as not
              working, and this is not an attendance record.
            </p>
          </section>

          <section className="card">
            <div className="step-nav">
              <button
                type="button"
                className="ghost"
                aria-label="Previous day"
                onClick={() => stepDay(-1)}
              >
                <Icon name="back" size={26} />
              </button>
              <div>
                <h2>{dayTitle}</h2>
                <p className="muted">{date === today ? "Today" : date.slice(0, 4)}</p>
              </div>
              <button
                type="button"
                className="ghost"
                aria-label="Next day"
                disabled={date >= today}
                onClick={() => stepDay(1)}
              >
                <Icon name="forward" size={26} />
              </button>
            </div>

            {error ? <p className="error" role="alert">{error}</p> : null}

            {day === null && !error ? (
              <p className="muted">Loading…</p>
            ) : day && day.length === 0 ? (
              <p className="muted">Nothing recorded on this day.</p>
            ) : null}

            {day && day.length > 0 && win ? (
              <>
                <div className="timeline-hours" aria-hidden="true">
                  {Array.from({ length: win.span / 2 + 1 }, (_, i) => (
                    <span key={i} style={{ left: `${((i * 2) / win.span) * 100}%` }}>
                      {win.start + i * 2}
                    </span>
                  ))}
                </div>

                <div
                  className="timeline-rows"
                  // One faint line per hour, however many hours are shown.
                  style={{ ["--hour-width" as string]: `${100 / win.span}%` }}
                >
                  {day.map((person) => (
                    <div key={person.employeeId} className="timeline-row">
                      <div className="timeline-name">
                        <strong>{person.displayName}</strong>
                        <span className="muted">{duration(person.trackedSeconds)} tracked</span>
                      </div>
                      <div className="timeline-track">
                        {person.entries.map((entry) => {
                          const box = place(date, entry, now, win);
                          if (!box) return null;
                          const detail = spanDetail(person.displayName, entry);
                          return (
                            // A button, not a span with a title: `title` is
                            // hover-only, and on the touch tablets a bar
                            // too narrow for its name could not be
                            // identified at all.
                            <button
                              type="button"
                              key={entry.sessionId}
                              className={[
                                "timeline-span",
                                entry.endedAt ? "" : "running",
                                box.clippedStart ? "clipped-start" : "",
                                box.clippedEnd ? "clipped-end" : "",
                                picked === entry.sessionId ? "picked" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              style={{
                                left: `${box.left}%`,
                                width: `${box.width}%`,
                                // backgroundColor, never `background`: the
                                // shorthand resets background-image and wipes
                                // out the stripes that mark a running span.
                                backgroundColor: `hsl(${hue(entry.task)} 45% 36%)`,
                              }}
                              title={detail}
                              aria-label={detail}
                              aria-pressed={picked === entry.sessionId}
                              onClick={() =>
                                setPicked((p) => (p === entry.sessionId ? null : entry.sessionId))
                              }
                            >
                              <span className="timeline-label">{entry.task}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <p className="timeline-detail" role="status">
                  {pickedDetail ?? "Tap a bar to see what it was."}
                </p>
              </>
            ) : null}

            <p className="hint">
              Blank stretches are time that was not tracked, which is not the
              same as time not worked. Striped bars are still running; a
              dashed edge carries on into the day before or after. Session
              totals are not expected to add up to a working day.
            </p>
          </section>
        </>
      ) : null}

      {tab === "calendar" ? (
        <>
          <section className="card">
            <div className="step-nav">
              <button
                type="button"
                className="ghost"
                aria-label="Previous month"
                onClick={() => shiftMonth(-1)}
              >
                <Icon name="back" size={26} />
              </button>
              <div>
                <h2>
                  {new Date(`${month}-01T00:00:00`).toLocaleDateString(undefined, {
                    month: "long",
                    year: "numeric",
                  })}
                </h2>
              </div>
              <button
                type="button"
                className="ghost"
                aria-label="Next month"
                onClick={() => shiftMonth(1)}
              >
                <Icon name="forward" size={26} />
              </button>
            </div>

            <div className="calendar">
              {WEEKDAYS.map((w) => (
                <span key={w} className="calendar-head">
                  {w}
                </span>
              ))}
              {grid.map((d, i) =>
                d === null ? (
                  <span key={`pad-${i}`} />
                ) : (
                  <button
                    key={d}
                    type="button"
                    className={[
                      "calendar-day",
                      d === date ? "selected" : "",
                      d > today ? "future" : "",
                      activeDays.includes(d) ? "has-work" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      setDate(d);
                      setTab("activity");
                    }}
                  >
                    {Number(d.slice(-2))}
                  </button>
                ),
              )}
            </div>
            <p className="hint">
              A dot marks a day with something recorded. Tap a day to open it
              under Activity.
            </p>
          </section>

          <section className="card">
            <h2>Export</h2>
            <div className="export-range">
              <div className="pair">
                <label>
                  Export from
                  <input
                    type="date"
                    value={from}
                    max={to || undefined}
                    onChange={(e) => setFrom(e.target.value)}
                    aria-label="Export from"
                  />
                </label>
                <label>
                  to
                  <input
                    type="date"
                    value={to}
                    min={from || undefined}
                    onChange={(e) => setTo(e.target.value)}
                    aria-label="Export to"
                  />
                </label>
              </div>
              {rangeValid ? (
                <div className="pair">
                  <a className="button-link primary" href={exportUrl("sessions")} download>
                    <Icon name="download" />
                    Sessions CSV
                  </a>
                  <a className="button-link primary" href={exportUrl("jobs")} download>
                    <Icon name="download" />
                    Jobs CSV
                  </a>
                </div>
              ) : (
                <p className="error">Set an end date on or after the start.</p>
              )}
            </div>
            <p className="hint">
              Both days are included. Picking a day above sets the range to
              that day. The two files answer different questions — sessions
              are one row per person per stretch, jobs are one row per unit
              of work — and they slice a range on different columns, so a job
              created just before the range with work inside it appears in
              one and not the other.
            </p>
          </section>
        </>
      ) : null}
    </main>
  );
}
